import type { paths } from "@riea-water/contracts";
import { problemSchema, type Problem } from "@riea-water/contracts";
import createClient, { type Middleware } from "openapi-fetch";

export interface ApiClientOptions {
  /** Origin and base path, default "/api/v1" (same origin behind Caddy). */
  baseUrl?: string;
  /** Returns the current bearer token, or null when signed out. */
  getToken: () => Promise<string | null> | string | null;
  /** Called on 401 so the shell can start a fresh sign-in. */
  onUnauthorised?: () => void;
  fetch?: typeof fetch;
}

/** An error response in RFC 9457 problem form, thrown by `unwrap`. */
export class ApiProblem extends Error {
  readonly problem: Problem;
  constructor(problem: Problem) {
    super(`${problem.status} ${problem.title}${problem.detail ? `: ${problem.detail}` : ""}`);
    this.name = "ApiProblem";
    this.problem = problem;
  }
}

export function createApiClient(options: ApiClientOptions) {
  const client = createClient<paths>({
    baseUrl: options.baseUrl ?? "/api/v1",
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });
  const auth: Middleware = {
    async onRequest({ request }) {
      const token = await options.getToken();
      if (token) request.headers.set("authorization", `Bearer ${token}`);
      request.headers.set("accept", "application/json, application/problem+json");
      return request;
    },
    onResponse({ response }) {
      if (response.status === 401) options.onUnauthorised?.();
      return response;
    },
  };
  client.use(auth);
  return client;
}

export type ApiClient = ReturnType<typeof createApiClient>;

/**
 * Unwraps an openapi-fetch result: data on success, an ApiProblem on an
 * error response (parsed as a problem detail where the body is one).
 */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || !result.response.ok) {
    const parsed = problemSchema.safeParse(result.error);
    throw new ApiProblem(
      parsed.success
        ? parsed.data
        : {
            type: "about:blank",
            title: result.response.statusText || "Request failed",
            status: result.response.status,
          },
    );
  }
  return result.data as T;
}
